import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
  Button as MuiButton, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Tooltip, Typography
} from '@mui/material';
import { ActiveFilters } from './activeFilters';
import { flashSx, POLL_MS, unseenSx } from './newArrivals';
import { useState } from 'react';
import {
  BooleanInput, Datagrid, DateField, FunctionField, List, SearchInput, TextField,
  useListContext, useNotify, useRecordContext, useRefresh, Button, TopToolbar, type RaRecord
} from 'react-admin';
import { callAction } from './dataProvider';

const EUR = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' });

/**
 * The orders queue - the admin's landing page, because it is the screen the
 * team works out of all day.
 *
 * Defaults to work outstanding: fulfilled orders are hidden until asked for,
 * so the list empties as it is worked rather than growing forever.
 *
 * Delivery and payment method are not shown. Since June 2022 every order
 * arrives through the qzila checkout and both columns hold opaque UUIDs, so a
 * column of them would be noise on 681 of the orders and useful on none of the
 * current ones. Resolving them means asking qzila, which is a separate job.
 */
const filters = [
  <SearchInput key="q" source="q" alwaysOn placeholder="Číslo objednávky alebo zákazník" />,
  <BooleanInput key="includeFulfilled" source="includeFulfilled" label="Zobraziť vybavené" />
];

function FulfilButton() {
  const record = useRecordContext<RaRecord & { fulfilled: boolean }>();
  const notify = useNotify();
  const refresh = useRefresh();

  if (record == null || record.fulfilled) return null;

  const fulfil = async () => {
    try {
      await callAction(`orders/${record.id}/fulfil`);
      notify('Objednávka označená ako vybavená.', { type: 'success' });
      refresh();
    } catch (error) {
      // 409 means someone else got there first, which is worth saying plainly
      // rather than showing a generic failure.
      notify(error instanceof Error ? error.message : 'Objednávku sa nepodarilo označiť ako vybavenú.', { type: 'warning' });
      refresh();
    }
  };

  return (
    <Button label="Označiť ako vybavenú" onClick={fulfil}>
      <CheckCircleIcon />
    </Button>
  );
}

/**
 * How the order was paid.
 *
 * A card order carries a Stripe reference and its status; an order placed any
 * other way carries neither, which is itself the answer - so the column shows
 * a dash rather than an empty cell that looks like missing data.
 */
function PaymentField({ id, status }: { id: string | null; status: string | null }) {
  if (id == null) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }

  const chip = status === 'succeeded'
    ? <Chip size="small" color="success" label="Zaplatená" />
    : <Chip size="small" variant="outlined" color="warning" label={status ?? 'neznámy stav'} />;

  return (
    <Tooltip title={`Stripe: ${id}`}>
      <span>{chip}</span>
    </Tooltip>
  );
}

function FulfilledField() {
  const record = useRecordContext<RaRecord & { fulfilled: boolean }>();
  if (record == null) return null;

  return record.fulfilled
    ? <Chip size="small" color="success" label="Vybavená" />
    : <Chip size="small" color="warning" label="Otvorená" variant="outlined" />;
}

/**
 * Clears the historical backlog off the queue.
 *
 * Eighteen thousand orders were never marked off, going back years, because the
 * old admin had no way to say "done". They are not work, and while they sit in
 * the queue the queue means nothing. Bounded by age rather than "everything",
 * so a recent order nobody has touched is left where it can still be seen.
 */
function FulfilOldButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const [asking, setAsking] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    setAsking(true);
    setCount(null);
    try {
      const result = await callAction(`orders/older-than/${DAYS}`, 'GET' as never);
      setCount((result as { total: number }).total);
    } catch {
      notify('Nepodarilo sa zistiť, koľkých objednávok sa to týka.', { type: 'warning' });
      setAsking(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await callAction('orders/fulfil-older-than', 'POST', { days: DAYS });
      notify(`Označených ako vybavené: ${(result as { updated: number }).updated}.`, { type: 'success' });
      setAsking(false);
      refresh();
    } catch {
      notify('Hromadné označenie sa nepodarilo.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button label="Označiť staršie ako mesiac za vybavené" onClick={ask}>
        <DoneAllIcon />
      </Button>

      <Dialog open={asking} onClose={() => setAsking(false)}>
        <DialogTitle>Označiť staršie objednávky za vybavené?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {count == null
              ? 'Zisťuje sa, koľkých objednávok sa to týka…'
              : count === 0
                ? 'Žiadna nevybavená objednávka nie je staršia ako mesiac.'
                : `Označí sa ${count} nevybavených objednávok starších ako mesiac. Novšie zostanú vo fronte. Túto akciu nie je možné vrátiť späť.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setAsking(false)}>Zrušiť</MuiButton>
          <MuiButton onClick={confirm} disabled={busy || count == null || count === 0}>
            Označiť
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** A month, which is what "old" means to the people working this queue. */
const DAYS = 30;

/**
 * A datagrid that marks what you have not dealt with.
 *
 * Two signals, both decided server-side and both per administrator. A green
 * edge stays on any order you have never opened, however old. A fading flash
 * marks anything ordered after the last order you saw - so it catches what
 * arrived overnight, not just what turned up while a tab happened to be open.
 */
function OrderGrid({ children }: { children: React.ReactNode }) {
  return (
    <Datagrid
      rowClick="show"
      bulkActionButtons={false}
      rowSx={record => ({
        // A standing mark on anything you have never opened, however old.
        ...(record['openedByMe'] === true ? {} : unseenSx),
        // And a fading one on anything ordered since you last looked - which
        // the server decides, so it survives a reload and catches orders that
        // arrived while nobody had the screen open.
        ...(record['isNew'] === true ? flashSx : {})
      })}
    >
      {children}
    </Datagrid>
  );
}

export function OrderList() {
  return (
    <List
      actions={<TopToolbar><FulfilOldButton /></TopToolbar>}
      filters={filters}
      // Newest first: a queue is worked from the top.
      sort={{ field: 'created', order: 'DESC' }}
      perPage={25}
      title="Objednávky"
      // The queue is left open all day, so it asks for new work rather than
      // waiting to be refreshed.
      queryOptions={{ refetchInterval: POLL_MS }}
    >
      <ActiveFilters
        ignore={['includeFulfilled']}
        labels={{ q: { label: 'Hľadanie' } }}
      />
      <OrderGrid>
        <TextField source="id" label="Číslo" />
        <DateField source="ordered" label="Objednané" showTime />
        <TextField source="name" label="Zákazník" />
        <TextField source="organizationName" label="Organizácia" emptyText="—" />
        <DateField source="invoiceSentAt" label="Faktúra odoslaná" emptyText="—" />
        {/* Blank on orders older than the totals, rather than a misleading 0 €. */}
        <FunctionField
          label="Platba"
          render={(record: RaRecord & { paymentId: string | null; paymentStatus: string | null }) =>
            <PaymentField id={record.paymentId} status={record.paymentStatus} />}
        />
        <FunctionField
          label="Suma s DPH"
          textAlign="right"
          render={(record: RaRecord & { totalWithVat: number | null }) =>
            record.totalWithVat == null ? '—' : EUR.format(record.totalWithVat)}
        />
        <FulfilledField />
        <FulfilButton />
      </OrderGrid>
    </List>
  );
}
